import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { brandConfig } from '../config/brands';
import { DEFAULT_SURVEY_QUESTIONS } from '../config/brands/types';
import { getCurrentUserProfile, updateProfile, requestEmailChange, uploadCroppedProfilePicture, deleteProfilePicture, getFullBodyPhotos, uploadFullBodyPhoto, deleteFullBodyPhoto, updateSafeMode, updateShowHelpTooltips, resetNavigationGuide, type FullBodyPhoto, type GeneratedActorInfo } from '../features/profile/api';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '../features/profile/types';
import { apiClient } from '../services/apiClient';
import { Label } from '../components/common/Label';
import { ToggleSwitch } from '../components/common/ToggleSwitch';
import { Tooltip } from '../components/common/Tooltip';
import { HelpTooltip } from '../components/common/HelpTooltip';
import { SingleSelectDropdown } from '../components/common/SingleSelectDropdown';
import { MultiSelectDropdown } from '../components/common/MultiSelectDropdown';
import { useSurveyConfig } from '../hooks/useUserProfile';
import { FaceCropModal } from '../features/profile/FaceCropModal';
import { ActorGenerationWizard, type WizardData, type InitialWizardData } from '../components/admin/actor-generation/ActorGenerationWizard';
import {
  generatePersonAsync as generatePerson,
  generateCharacterSheetAsync as generateCharacterSheet,
  suggestNameAsync as suggestName,
  checkExclusiveActorName,
  listExclusiveActors,
  triggerExclusiveActorFaceScan,
} from '../features/exclusive-actors/api';
import type { ExclusiveActor } from '../features/exclusive-actors/types';
import { useCreateExclusiveActor, useUpdateExclusiveActor, useDeleteExclusiveActor } from '../features/exclusive-actors';
import { useToast } from '../hooks/useToast';
import { useCanvaAuthStatus, useDisconnectCanva, useCanvaAuth, useCanvaAuthListener } from '../features/canva/hooks';
import { useYouTubeAuthStatus, useDisconnectYouTube, useYouTubeAuth, useYouTubeAuthListener } from '../features/youtube/hooks';
import { ActorDetailModal, type ActorInfo } from '../components/shared/ActorDetailModal';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { ActorFormModal, type ActorLike } from '../components/admin/ActorFormModal';
import { useImpersonation } from '../features/impersonation/useImpersonation';
import { useMyOrganization } from '../features/organizations';
import { toCharacterSheetFiles } from '../features/actors/characterSheet';
import { isCharacterActor } from '../features/actors/actorAttributes';
import { Check, X, ChevronRight } from 'lucide-react';
import '../styles/inline-edit.css';
import '../styles/buttons.css';
import '../styles/admin-pages.css';
import './ProfilePage.css';
import '../styles/badges.css';
import CreatorSection from '../features/acp/CreatorSection';

// プロフィールに旧アンケート設問 (利用目的=user_type / 性別=gender) の行を出すかは
// ブランドの有効設問に応じて出し分ける。これらを使わないブランド (SAITE/usodeki 等) では
// 非表示にする (送信しても backend の設問バリデーションで拒否されるため)。
const SURVEY_QUESTION_KEYS = brandConfig.surveyQuestions ?? DEFAULT_SURVEY_QUESTIONS;
const SHOW_USER_TYPE_ROW = SURVEY_QUESTION_KEYS.includes('user_type');
const SHOW_GENDER_ROW = SURVEY_QUESTION_KEYS.includes('gender');
// DB 駆動の複数選択設問 (SAITE の 利用目的/きっかけ)。選択肢は /users/me/survey から引く
const SHOW_USAGE_PURPOSE_ROW = SURVEY_QUESTION_KEYS.includes('usage_purpose');
const SHOW_DISCOVERY_CHANNEL_ROW = SURVEY_QUESTION_KEYS.includes('discovery_channel');

const POINTS_VISIBILITY_KEY = 'points_visibility';
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB（圧縮前の元ファイル）

/**
 * 代行アクセス中に出す注意書き。
 * 「マイページ = 自分の (actor の) プロフィール」であって、代行先 org のユーザーでは
 * ない事を明示する。代行中以外は描画しない。
 */
function ImpersonationProfileNotice({ profile }: { profile: UserProfile | null }) {
  const { currentSession } = useImpersonation();
  if (!currentSession) return null;
  const targetLabel =
    currentSession.target_organization?.display_name ||
    currentSession.target_organization?.name ||
    '代行先組織';
  const actorLabel = profile?.display_name || profile?.email || 'あなた本人';
  return (
    <div className="profile-impersonation-notice" role="note">
      <strong>{targetLabel}</strong> の代行アクセス中に表示しているのは、代行している
      <strong> あなた自身 ({actorLabel}) のプロフィール</strong>です。
      代行先組織のユーザーではありません。ここで変更した内容はあなた本人のアカウントに
      反映されます。
    </div>
  );
}

export default function ProfilePage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const shouldHighlightHelpTooltips = searchParams.get('highlight') === 'help-tooltips';
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // 編集用のステート
  const [displayName, setDisplayName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);

  // 画像アップロード用
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  // 全身写真用
  const fullBodyFileInputRef = useRef<HTMLInputElement>(null);
  const [fullBodyPhotos, setFullBodyPhotos] = useState<FullBodyPhoto[]>([]);
  const [isUploadingFullBody, setIsUploadingFullBody] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [isDraggingFullBody, setIsDraggingFullBody] = useState(false);

  // AI俳優生成ウィザード用
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardInitialData, setWizardInitialData] = useState<InitialWizardData | undefined>(undefined);
  const [sourcePhotoId, setSourcePhotoId] = useState<string | null>(null);  // 紐付け用
  const createActorMutation = useCreateExclusiveActor();

  // AI俳優詳細モーダル用
  const [exclusiveActors, setExclusiveActors] = useState<ExclusiveActor[]>([]);
  const [selectedActor, setSelectedActor] = useState<ActorInfo | null>(null);
  const [isActorDetailOpen, setIsActorDetailOpen] = useState(false);

  // AI俳優編集モーダル用
  const [editingActor, setEditingActor] = useState<ActorLike | null>(null);
  const [isActorFormOpen, setIsActorFormOpen] = useState(false);
  const updateActorMutation = useUpdateExclusiveActor();
  const deleteActorMutation = useDeleteExclusiveActor();

  // 全身写真ライトボックス用
  const [lightboxPhotoUrl, setLightboxPhotoUrl] = useState<string | null>(null);

  // ポイント表示設定
  const [showPoints, setShowPoints] = useState(() => {
    const saved = localStorage.getItem(POINTS_VISIBILITY_KEY);
    return saved === null ? true : saved === 'true';
  });

  // セーフモード
  const [safeMode, setSafeMode] = useState(true);
  // partner 強制 or org 不許可でロック中はトグル自体を非表示にする
  const [safeModeLocked, setSafeModeLocked] = useState(false);
  const [showSafeModeConfirm, setShowSafeModeConfirm] = useState(false);
  const [isSavingSafeMode, setIsSavingSafeMode] = useState(false);

  // ヘルプツールチップ表示
  const [showHelpTooltips, setShowHelpTooltips] = useState(true);
  const [isSavingHelpTooltips, setIsSavingHelpTooltips] = useState(false);

  // ナビゲーションガイド再生
  const navigate = useNavigate();
  const [isReplayingGuide, setIsReplayingGuide] = useState(false);
  const handleReplayNavigationGuide = async () => {
    setIsReplayingGuide(true);
    try {
      await resetNavigationGuide();
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      navigate('/');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'ガイドの再生に失敗しました');
    } finally {
      setIsReplayingGuide(false);
    }
  };

  // 属性編集
  const [isSavingAttribute, setIsSavingAttribute] = useState<string | null>(null);

  // 所属組織 (代行アクセス中は「自分の組織」ではなく代行先が返るため非表示にする)
  const { currentSession: impersonationSession } = useImpersonation();
  const organization = useMyOrganization().data;

  // Canva連携
  const { data: canvaStatus, isLoading: isCanvaStatusLoading } = useCanvaAuthStatus();
  const disconnectCanvaMutation = useDisconnectCanva();
  const { startAuth: startCanvaAuth, isAuthenticating: isCanvaAuthenticating } = useCanvaAuth();
  useCanvaAuthListener(); // 認証完了時に自動的にステータスを更新

  // YouTube連携
  const { data: youtubeStatus, isLoading: isYouTubeStatusLoading } = useYouTubeAuthStatus();
  const disconnectYouTubeMutation = useDisconnectYouTube();
  const { startAuth: startYouTubeAuth, isAuthenticating: isYouTubeAuthenticating } = useYouTubeAuth();
  useYouTubeAuthListener(); // 認証完了時に自動的にステータスを更新

  useEffect(() => {
    loadProfile();
    loadFullBodyPhotos();
    loadExclusiveActors();
  }, []);

  useEffect(() => {
    if (!shouldHighlightHelpTooltips || isLoading) return;
    const target = document.getElementById('help-tooltips-setting');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [shouldHighlightHelpTooltips, isLoading]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const data = await getCurrentUserProfile();
      setProfile(data);
      setDisplayName(data.display_name);
      setSafeMode(data.safe_mode ?? true);
      setSafeModeLocked(data.safe_mode_locked ?? false);
      setShowHelpTooltips(data.show_help_tooltips ?? true);
    } catch (err: any) {
      toast.error(err.message || 'プロフィールの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFullBodyPhotos = async () => {
    try {
      const photos = await getFullBodyPhotos();
      setFullBodyPhotos(photos);
    } catch (err) {
      console.error('Failed to load full body photos:', err);
    }
  };

  const loadExclusiveActors = async () => {
    try {
      const response = await listExclusiveActors();
      setExclusiveActors(response.actors);
    } catch (err) {
      console.error('Failed to load exclusive actors:', err);
    }
  };

  const handleActorTagClick = (actorInfo: GeneratedActorInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    // 専属AI俳優一覧から詳細情報を探す
    const actor = exclusiveActors.find(a => a.id === actorInfo.id);
    if (actor) {
      setSelectedActor(actor);
      setIsActorDetailOpen(true);
    }
  };

  const handleEditActor = (actor: ActorInfo) => {
    // ActorInfo を ActorLike に変換（互換性あり）
    setEditingActor(actor as ActorLike);
    setIsActorFormOpen(true);
  };

  const handleActorFormSuccess = () => {
    setIsActorFormOpen(false);
    setEditingActor(null);
    loadExclusiveActors();
    loadFullBodyPhotos();
    toast.success(`${brandConfig.labels.aiActor}を更新しました`);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      toast.error('表示名を入力してください');
      return;
    }

    try {
      setIsSaving(true);
      const updated = await updateProfile({
        display_name: displayName,
      });
      setProfile(updated);
      toast.success('プロフィールを更新しました');
      setIsEditingDisplayName(false);
    } catch (err: any) {
      toast.error(err.message || 'プロフィールの更新に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newEmail.trim()) {
      toast.error('新しいメールアドレスを入力してください');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('有効なメールアドレスを入力してください');
      return;
    }

    if (!profile) return;

    try {
      setIsSaving(true);
      await requestEmailChange(profile.id, { new_email: newEmail });
      toast.success('確認メールを送信しました。メールに記載されたリンクをクリックして変更を完了してください。');
      setNewEmail('');
      setShowEmailChange(false);
    } catch (err: any) {
      toast.error(err.message || 'メールアドレス変更リクエストに失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePointsVisibility = () => {
    setShowPoints((prev) => {
      const newValue = !prev;
      localStorage.setItem(POINTS_VISIBILITY_KEY, String(newValue));
      // カスタムイベントをディスパッチして、AppLayoutに変更を通知
      window.dispatchEvent(new Event('pointsVisibilityChanged'));
      return newValue;
    });
  };

  const toggleSafeMode = () => {
    if (safeMode) {
      // ONからOFFにする場合は確認ダイアログを表示
      setShowSafeModeConfirm(true);
    } else {
      // OFFからONにする場合は即時切り替え
      handleSafeModeChange(true);
    }
  };

  const toggleShowHelpTooltips = async () => {
    const newValue = !showHelpTooltips;
    const previousValue = showHelpTooltips;
    setShowHelpTooltips(newValue);
    setIsSavingHelpTooltips(true);
    try {
      await updateShowHelpTooltips(newValue);
      setProfile((prev) => (prev ? { ...prev, show_help_tooltips: newValue } : prev));
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      toast.success(newValue ? 'ヘルプを表示します' : 'ヘルプを非表示にしました');
    } catch (err: any) {
      setShowHelpTooltips(previousValue);
      toast.error(err?.response?.data?.error || 'ヘルプ設定の変更に失敗しました');
    } finally {
      setIsSavingHelpTooltips(false);
    }
  };

  const handleSafeModeChange = async (newValue: boolean) => {
    try {
      setIsSavingSafeMode(true);
      await updateSafeMode(newValue);
      setSafeMode(newValue);
      toast.success(newValue ? 'セーフモードを有効にしました' : 'セーフモードを解除しました');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'セーフモードの変更に失敗しました');
    } finally {
      setIsSavingSafeMode(false);
    }
  };

  // DB 駆動の複数選択設問の選択肢・付与額 (行を出すブランドのときだけ取得)
  const surveyConfigQuery = useSurveyConfig(SHOW_USAGE_PURPOSE_ROW || SHOW_DISCOVERY_CHANNEL_ROW);
  const surveyRewardPoints = surveyConfigQuery.data?.credits_per_answer ?? 150;
  const dbSurveyOptions = (key: string) =>
    (surveyConfigQuery.data?.questions.find((q) => q.key === key)?.options ?? [])
      .map((o) => ({ value: o.value, label: o.label ?? o.value }));

  const handleMultiAttributeChange = async (question: 'usage_purpose' | 'discovery_channel', values: string[]) => {
    const prevValues = profile?.[`attribute_${question}`] ?? [];
    setProfile(prev => prev ? { ...prev, [`attribute_${question}`]: values } : prev);
    // backend は空配列を受け付けないため全解除は保存しない (次の選択でまとめて保存される)
    if (values.length === 0) return;
    setIsSavingAttribute(question);
    try {
      const res = await apiClient.post('/users/me/attributes', { question, answer: values });
      const granted = res.data.credits_granted;
      if (granted > 0) {
        setProfile(prev => prev ? { ...prev, [`attribute_${question}_answered`]: true } : prev);
        toast.success(`${granted}ポイントを獲得しました！`);
      }
      // AppLayout のアンケート進行判定・ポイント表示を最新化する
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      queryClient.invalidateQueries({ queryKey: ['credits'] });
    } catch (err: any) {
      setProfile(prev => prev ? { ...prev, [`attribute_${question}`]: prevValues } : prev);
      toast.error(err?.response?.data?.error || '回答の保存に失敗しました');
    } finally {
      setIsSavingAttribute(null);
    }
  };

  const handleAttributeChange = async (question: 'user_type' | 'gender', answer: string) => {
    if (!answer) return;
    setIsSavingAttribute(question);
    try {
      const res = await apiClient.post('/users/me/attributes', { question, answer });
      const granted = res.data.credits_granted;
      setProfile(prev => prev ? {
        ...prev,
        [`attribute_${question === 'user_type' ? 'user_type' : 'gender'}`]: answer,
      } : prev);
      if (granted > 0) {
        toast.success(`${granted}ポイントを獲得しました！`);
      } else {
        toast.success(res.data.message || '回答を更新しました');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || '回答の保存に失敗しました');
    } finally {
      setIsSavingAttribute(null);
    }
  };

  const handleImageClick = () => {
    if (!isUploadingImage) {
      fileInputRef.current?.click();
    }
  };

  // 画像を事前に圧縮する関数
  const compressImage = (file: File, maxSize: number = 1200): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // 画像が小さければそのまま返す
          if (img.width <= maxSize && img.height <= maxSize && file.size < 500 * 1024) {
            resolve(file);
            return;
          }

          // リサイズ比率を計算
          const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const width = Math.round(img.width * ratio);
          const height = Math.round(img.height * ratio);

          // キャンバスで圧縮
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Blobに変換
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ファイルタイプの検証
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('対応している画像形式はJPEG、PNG、GIF、WebPです');
      return;
    }

    // ファイルサイズの検証（元ファイルは10MBまで許可、圧縮後に制限）
    if (file.size > MAX_FILE_SIZE) {
      toast.error('ファイルサイズは10MB以下にしてください');
      return;
    }

    try {
      // 大きな画像は事前に圧縮
      const processedFile = await compressImage(file);
      setSelectedFile(processedFile);
      setShowCropModal(true);
    } catch (err) {
      toast.error('画像の処理に失敗しました');
    }

    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropConfirm = async (croppedBase64: string) => {
    setShowCropModal(false);
    setSelectedFile(null);
    setIsUploadingImage(true);

    try {
      const result = await uploadCroppedProfilePicture(croppedBase64);
      setProfile(result.user);
      toast.success('プロフィール画像を更新しました');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'プロフィール画像のアップロードに失敗しました');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedFile(null);
  };

  const handleClearImage = async () => {
    if (!profile?.picture_url) return;

    setIsDeletingImage(true);

    try {
      const result = await deleteProfilePicture();
      setProfile(result.user);
      toast.success('プロフィール画像を削除しました');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'プロフィール画像の削除に失敗しました');
    } finally {
      setIsDeletingImage(false);
    }
  };

  // 全身写真のアップロード
  const handleFullBodyUploadClick = () => {
    if (!isUploadingFullBody) {
      fullBodyFileInputRef.current?.click();
    }
  };

  // 全身写真用の圧縮関数（大きめにリサイズ）
  const compressFullBodyImage = (file: File, maxSize: number = 2048): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // 画像が小さく、ファイルサイズも小さければそのまま返す
          if (img.width <= maxSize && img.height <= maxSize && file.size < 2 * 1024 * 1024) {
            resolve(file);
            return;
          }

          // リサイズ比率を計算
          const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const width = Math.round(img.width * ratio);
          const height = Math.round(img.height * ratio);

          // キャンバスで圧縮
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Blobに変換
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.9
          );
        };
        img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
      reader.readAsDataURL(file);
    });
  };

  const handleFullBodyFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ファイルタイプの検証
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('対応している画像形式はJPEG、PNG、GIF、WebPです');
      return;
    }

    setIsUploadingFullBody(true);

    try {
      // 大きな画像は圧縮
      const processedFile = await compressFullBodyImage(file);
      const photo = await uploadFullBodyPhoto(processedFile);
      setFullBodyPhotos((prev) => [photo, ...prev]);
      toast.success('全身写真をアップロードしました');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || '全身写真のアップロードに失敗しました');
    } finally {
      setIsUploadingFullBody(false);
      // ファイル入力をリセット
      if (fullBodyFileInputRef.current) {
        fullBodyFileInputRef.current.value = '';
      }
    }
  };

  // 全身写真のドラッグ&ドロップ
  const handleFullBodyDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFullBody(true);
  };

  const handleFullBodyDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFullBody(false);
  };

  const handleFullBodyDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFullBody(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // ファイルタイプの検証
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('対応している画像形式はJPEG、PNG、GIF、WebPです');
      return;
    }

    setIsUploadingFullBody(true);

    try {
      // 大きな画像は圧縮
      const processedFile = await compressFullBodyImage(file);
      const photo = await uploadFullBodyPhoto(processedFile);
      setFullBodyPhotos((prev) => [photo, ...prev]);
      toast.success('全身写真をアップロードしました');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || '全身写真のアップロードに失敗しました');
    } finally {
      setIsUploadingFullBody(false);
    }
  };

  const handleDeleteFullBodyPhoto = async (photoId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    setDeletingPhotoId(photoId);

    try {
      await deleteFullBodyPhoto(photoId);
      setFullBodyPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success('全身写真を削除しました');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || '全身写真の削除に失敗しました');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleFullBodyPhotoClick = async (photo: FullBodyPhoto) => {
    // 画像URLをBase64に変換してウィザードを開く
    try {
      const res = await fetch(photo.url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setWizardInitialData({
          personImageMode: 'upload',
          personImageBase64: base64,
          personMimeType: blob.type || 'image/jpeg',
        });
        setSourcePhotoId(photo.id);  // 紐付け用にIDを保持
        setIsWizardOpen(true);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Failed to load image:', err);
      toast.error('画像の読み込みに失敗しました');
    }
  };

  const handleWizardClose = () => {
    setIsWizardOpen(false);
    setWizardInitialData(undefined);
    setSourcePhotoId(null);
  };

  // ウィザード完了時の処理
  const handleWizardComplete = useCallback(
    async (data: WizardData) => {
      if (!data.characterSheetBase64 || !data.faceImageBase64) {
        alert('画像データが不足しています');
        return;
      }

      if (!isCharacterActor({ ethnicity: data.ethnicity }) && data.gender !== 'male' && data.gender !== 'female') {
        alert('性別を選択してください');
        return;
      }

      try {
        // Base64をFileに変換
        const { characterSheetFile, faceImageFile } = await toCharacterSheetFiles({
          characterSheetBase64: data.characterSheetBase64,
          characterSheetMimeType: data.characterSheetMimeType,
          faceImageBase64: data.faceImageBase64,
        });

        // AI俳優を作成
        await createActorMutation.mutateAsync({
          character_sheet: characterSheetFile,
          face_image: faceImageFile,
          name: data.internalName,
          display_name: data.displayName,
          display_name_en: data.displayNameEn || undefined,
          gender: data.gender !== 'none' ? data.gender : undefined,
          min_age: data.ageMin ?? undefined,
          max_age: data.ageMax ?? undefined,
          ethnicity: data.ethnicity || undefined,
          tags: data.tags,
          body_type: data.bodyType !== 'none' ? data.bodyType : undefined,
          physical_features: data.physicalFeatures !== 'none' ? data.physicalFeatures : undefined,
          // キャラクターの特徴・設定テキスト (Step5・キャラクターのみ)。AIプロンプトへそのまま追加される
          character_traits: data.characterTraits.trim() || undefined,
          generation_prompt: data.prompt || undefined,
          source_photo_id: sourcePhotoId || undefined,  // 全身写真との紐付け
        });

        // 成功したらウィザードを閉じて、全身写真を再読み込み
        setIsWizardOpen(false);
        setWizardInitialData(undefined);
        setSourcePhotoId(null);
        loadFullBodyPhotos();  // 紐付け情報を反映するため再読み込み
        loadExclusiveActors();  // AI俳優一覧も再読み込み
        toast.success(`専属${brandConfig.labels.aiActor}を作成しました`);
      } catch (err: any) {
        console.error('Failed to create actor:', err);
        toast.error(err?.response?.data?.error || err?.message || '作成に失敗しました');
      }
    },
    [createActorMutation, toast, sourcePhotoId]
  );

  if (isLoading) {
    return (
      <div className="page admin-page">
        <div className="admin-loading">プロフィールを読み込み中...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page admin-page">
        <div className="admin-error">プロフィールが見つかりません</div>
      </div>
    );
  }

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/single_neutral_actions_edit_1.svg" alt="" className="page-title-icon" />
            マイページ
          </h1>
        </div>
      </div>

      <ImpersonationProfileNotice profile={profile} />

      <div className="profile-two-col">
        <div>
      <h2 className="profile-section-title">プロフィール設定</h2>

      <div className="profile-table-container">
        <table className="profile-table">
          <tbody>
            {/* プロフィール画像 */}
            <tr>
              <td className="profile-label">プロフィール画像</td>
              <td className="profile-value">
                <div className="profile-avatar-container">
                  {profile.picture_url ? (
                    <>
                      <img
                        src={profile.picture_url}
                        alt="プロフィール画像"
                        className="profile-avatar"
                      />
                      {/* ホバー時のクリアオーバーレイ */}
                      <div
                        className="profile-avatar-clear-overlay"
                        onClick={handleClearImage}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleClearImage()}
                      >
                        {isDeletingImage ? (
                          <span className="upload-spinner"></span>
                        ) : (
                          <>
                            <img src="/remove_bold.svg" alt="削除" className="profile-avatar-clear-icon" />
                            <span className="profile-avatar-clear-text">クリア</span>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="profile-avatar-placeholder">
                      <span>未設定</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="profile-avatar-edit-btn"
                    onClick={handleImageClick}
                    disabled={isUploadingImage || isDeletingImage}
                    title="画像を変更"
                  >
                    {isUploadingImage ? (
                      <span className="upload-spinner"></span>
                    ) : (
                      <img src="/pencil_1.svg" alt="編集" style={{ width: '14px', height: '14px' }} />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <p className="profile-avatar-hint">JPEG, PNG, GIF, WebP（10MB以下、自動圧縮）</p>
              </td>
            </tr>

            {/* 表示名 */}
            <tr>
              <td className="profile-label">表示名</td>
              <td className="profile-value">
                {isEditingDisplayName ? (
                  <form onSubmit={handleUpdateProfile} className="profile-edit-form">
                    <div className="inline-edit-container">
                      <input
                        type="text"
                        className="inline-edit-input"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="表示名を入力"
                        maxLength={50}
                        required
                        autoFocus
                      />
                      <button
                        type="submit"
                        className="inline-save-button"
                        disabled={isSaving || displayName === profile.display_name}
                        title="保存"
                      >
                        <Check size={18} className="inline-action-icon" />
                      </button>
                      <button
                        type="button"
                        className="inline-cancel-button"
                        onClick={() => {
                          setDisplayName(profile.display_name);
                          setIsEditingDisplayName(false);
                        }}
                        disabled={isSaving}
                        title="キャンセル"
                      >
                        <X size={18} className="inline-action-icon" />
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="profile-value-with-edit">
                    <span>{profile.display_name || '未設定'}</span>
                    <button
                      type="button"
                      className="btn-icon-edit"
                      onClick={() => setIsEditingDisplayName(true)}
                      disabled={isSaving}
                    >
                      <img src="/common_file_edit.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                )}
              </td>
            </tr>

            {/* メールアドレス */}
            <tr>
              <td className="profile-label">メールアドレス</td>
              <td className="profile-value">
                {showEmailChange ? (
                  <form onSubmit={handleRequestEmailChange} className="profile-edit-form">
                    <div className="form-group">
                      <label htmlFor="newEmail" className="form-label">
                        新しいメールアドレス
                      </label>
                      <input
                        type="email"
                        id="newEmail"
                        className="form-input"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="new-email@example.com"
                        required
                        autoFocus
                      />
                      <p className="form-help">
                        確認メールが新しいメールアドレスに送信されます。メール内のリンクをクリックして変更を完了してください。
                      </p>
                    </div>
                    <div className="form-actions-inline">
                      <button
                        type="submit"
                        className="btn-modal"
                        disabled={isSaving}
                      >
                        {isSaving ? '送信中...' : '確認メールを送信'}
                      </button>
                      <button
                        type="button"
                        className="inline-cancel-button"
                        onClick={() => {
                          setShowEmailChange(false);
                          setNewEmail('');
                        }}
                        disabled={isSaving}
                        title="キャンセル"
                      >
                        <X size={18} className="inline-action-icon" />
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="profile-value-with-edit">
                    <span>{profile.email}</span>
                    <button
                      type="button"
                      className="btn-icon-edit"
                      onClick={() => setShowEmailChange(true)}
                      disabled={isSaving}
                    >
                      <img src="/common_file_edit.svg" alt="編集" style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                )}
              </td>
            </tr>

            {/* 所属組織 (代行アクセス中・組織を見せないブランドは表示しない) */}
            {!impersonationSession && organization && !brandConfig.features.simpleProfilePage && (
              <tr>
                <td className="profile-label">所属組織</td>
                <td className="profile-value">
                  <Link to="/organizations" className="profile-link-value">
                    <span>{organization.display_name || organization.name}</span>
                    <ChevronRight size={16} className="profile-link-chevron" />
                  </Link>
                </td>
              </tr>
            )}

            {/* 権限 (組織を見せないブランドは非表示) */}
            {!brandConfig.features.simpleProfilePage && (
            <tr>
              <td className="profile-label">権限</td>
              <td className="profile-value">
                <Label variant={profile.role === 'admin' ? 'blue' : 'gray'}>
                  {profile.role === 'admin' ? '管理者' : 'ユーザー'}
                </Label>
              </td>
            </tr>
            )}

            {/* 全身写真 (カメオ導線を持たないブランドは非表示) */}
            {!brandConfig.features.simpleProfilePage && (
            <tr>
              <td className="profile-label">全身写真（カメオ）</td>
              <td className="profile-value">
                <input
                  ref={fullBodyFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFullBodyFileChange}
                  style={{ display: 'none' }}
                />
                <div className="full-body-rows">
                  {fullBodyPhotos.length === 0 ? (
                    /* 画像が1枚もない時 */
                    <div className="full-body-row">
                      <div
                        className={`full-body-upload-card ${isDraggingFullBody ? 'dragging' : ''}`}
                        onClick={handleFullBodyUploadClick}
                        onDragOver={handleFullBodyDragOver}
                        onDragLeave={handleFullBodyDragLeave}
                        onDrop={handleFullBodyDrop}
                      >
                        {isUploadingFullBody ? (
                          <span className="upload-spinner"></span>
                        ) : (
                          <>
                            <span className="full-body-upload-icon">+</span>
                            <span className="full-body-upload-text">写真を追加</span>
                          </>
                        )}
                      </div>
                      <div className="full-body-actors-area">
                        <span className="full-body-actors-label">あなたも専属{brandConfig.labels.aiActor}になろう！</span>
                        <div className="full-body-actor-placeholder" />
                      </div>
                    </div>
                  ) : (
                    /* 画像が1枚以上ある時 */
                    <>
                      {fullBodyPhotos.map((photo) => (
                        <div key={photo.id} className="full-body-row">
                          <div className="full-body-photo-card">
                            <img
                              src={photo.thumbnail_url}
                              alt="全身写真"
                              onClick={() => setLightboxPhotoUrl(photo.url)}
                            />
                            <button
                              type="button"
                              className="full-body-photo-delete"
                              onClick={(e) => handleDeleteFullBodyPhoto(photo.id, e)}
                              disabled={deletingPhotoId === photo.id}
                            >
                              {deletingPhotoId === photo.id ? (
                                <span className="upload-spinner" style={{ width: '10px', height: '10px' }}></span>
                              ) : (
                                <img src="/bin_1_white.svg" alt="削除" />
                              )}
                            </button>
                          </div>
                          <div className="full-body-actors-area">
                            {photo.generated_actors && photo.generated_actors.length > 0 ? (
                              <>
                                <span className="full-body-actors-label">{brandConfig.labels.aiActors}</span>
                                <div className="full-body-actors-list">
                                  {photo.generated_actors.map(actor => {
                                    const actorDetail = exclusiveActors.find(a => a.id === actor.id);
                                    const faceUrl = actorDetail?.face_image_bucket && actorDetail?.face_image_path
                                      ? `https://lirsarznxaidebqhgxch.supabase.co/storage/v1/object/public/${actorDetail.face_image_bucket}/${actorDetail.face_image_path}`
                                      : null;
                                    return (
                                      <Tooltip key={actor.id} content={actor.display_name} placement="bottom">
                                        <button
                                          type="button"
                                          className="full-body-actor-circle"
                                          onClick={(e) => handleActorTagClick(actor, e)}
                                        >
                                          {faceUrl ? (
                                            <img src={faceUrl} alt={actor.display_name} />
                                          ) : (
                                            <div className="full-body-actor-placeholder-inner" />
                                          )}
                                        </button>
                                      </Tooltip>
                                    );
                                  })}
                                  <button
                                    type="button"
                                    className="full-body-actor-add-btn"
                                    onClick={() => handleFullBodyPhotoClick(photo)}
                                  >
                                    +
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="full-body-actors-label">あなたも専属{brandConfig.labels.aiActor}になろう！</span>
                                <button
                                  type="button"
                                  className="full-body-actor-add-btn"
                                  onClick={() => handleFullBodyPhotoClick(photo)}
                                >
                                  +
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* 追加ボタン行 */}
                      <div className="full-body-row full-body-row-add">
                        <button
                          type="button"
                          className="full-body-add-btn"
                          onClick={handleFullBodyUploadClick}
                          disabled={isUploadingFullBody}
                        >
                          {isUploadingFullBody ? (
                            <span className="upload-spinner" style={{ width: '14px', height: '14px' }}></span>
                          ) : (
                            <>+ 写真を追加</>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </td>
            </tr>
            )}

            {/* 利用目的 (旧設問。使わないブランドでは非表示) */}
            {SHOW_USER_TYPE_ROW && (
            <tr>
              <td className="profile-label">利用目的</td>
              <td className="profile-value">
                <div className="profile-attribute-row">
                  <SingleSelectDropdown
                    label="利用目的"
                    options={[
                      { value: 'corporate', label: '仕事のために使いたい' },
                      { value: 'individual', label: '個人で楽しむために使いたい' },
                    ]}
                    value={profile.attribute_user_type ?? ''}
                    onChange={(v) => handleAttributeChange('user_type', v)}
                    labelOnly
                    className="profile-attribute-dropdown"
                  />
                  {!profile.attribute_user_type && !profile.attribute_user_type_answered && (
                    <Tooltip content="回答すると150ポイントGET!" placement="top">
                      <img src="/money_bag_dollar.svg" alt="" className="profile-attribute-incentive" />
                    </Tooltip>
                  )}
                  {isSavingAttribute === 'user_type' && <span className="upload-spinner" style={{ width: '14px', height: '14px' }} />}
                </div>
              </td>
            </tr>
            )}

            {/* 性別 (旧設問。使わないブランドでは非表示) */}
            {SHOW_GENDER_ROW && (
            <tr>
              <td className="profile-label">性別</td>
              <td className="profile-value">
                <div className="profile-attribute-row">
                  <SingleSelectDropdown
                    label="性別"
                    options={[
                      { value: 'male', label: '男性' },
                      { value: 'female', label: '女性' },
                    ]}
                    value={profile.attribute_gender ?? ''}
                    onChange={(v) => handleAttributeChange('gender', v)}
                    labelOnly
                    className="profile-attribute-dropdown"
                  />
                  {!profile.attribute_gender && !profile.attribute_gender_answered && (
                    <Tooltip content="回答すると150ポイントGET!" placement="top">
                      <img src="/money_bag_dollar.svg" alt="" className="profile-attribute-incentive" />
                    </Tooltip>
                  )}
                  {isSavingAttribute === 'gender' && <span className="upload-spinner" style={{ width: '14px', height: '14px' }} />}
                </div>
              </td>
            </tr>
            )}

            {/* 利用目的 (SAITE の複数選択設問。選択肢は survey_options 由来) */}
            {SHOW_USAGE_PURPOSE_ROW && surveyConfigQuery.data && (
            <tr>
              <td className="profile-label">利用目的</td>
              <td className="profile-value">
                <div className="profile-attribute-row">
                  <MultiSelectDropdown
                    label="利用目的"
                    options={dbSurveyOptions('usage_purpose')}
                    selectedValues={profile.attribute_usage_purpose ?? []}
                    onChange={(v) => handleMultiAttributeChange('usage_purpose', v)}
                    className="profile-attribute-dropdown"
                    fitContent
                  />
                  {!profile.attribute_usage_purpose?.length && !profile.attribute_usage_purpose_answered && (
                    <Tooltip content={`回答すると${surveyRewardPoints}ポイントGET!`} placement="top">
                      <img src="/money_bag_dollar.svg" alt="" className="profile-attribute-incentive" />
                    </Tooltip>
                  )}
                  {isSavingAttribute === 'usage_purpose' && <span className="upload-spinner" style={{ width: '14px', height: '14px' }} />}
                </div>
              </td>
            </tr>
            )}

            {/* きっかけ (SAITE の複数選択設問。選択肢は survey_options 由来) */}
            {SHOW_DISCOVERY_CHANNEL_ROW && surveyConfigQuery.data && (
            <tr>
              <td className="profile-label">きっかけ</td>
              <td className="profile-value">
                <div className="profile-attribute-row">
                  <MultiSelectDropdown
                    label="きっかけ"
                    options={dbSurveyOptions('discovery_channel')}
                    selectedValues={profile.attribute_discovery_channel ?? []}
                    onChange={(v) => handleMultiAttributeChange('discovery_channel', v)}
                    className="profile-attribute-dropdown"
                    fitContent
                  />
                  {!profile.attribute_discovery_channel?.length && !profile.attribute_discovery_channel_answered && (
                    <Tooltip content={`回答すると${surveyRewardPoints}ポイントGET!`} placement="top">
                      <img src="/money_bag_dollar.svg" alt="" className="profile-attribute-incentive" />
                    </Tooltip>
                  )}
                  {isSavingAttribute === 'discovery_channel' && <span className="upload-spinner" style={{ width: '14px', height: '14px' }} />}
                </div>
              </td>
            </tr>
            )}

            {/* アカウントID */}
            <tr>
              <td className="profile-label">アカウントID</td>
              <td className="profile-value">
                <code className="code-value">{profile.id}</code>
              </td>
            </tr>

            {/* 作成日時 */}
            <tr>
              <td className="profile-label">作成日時</td>
              <td className="profile-value">
                {new Date(profile.created_at).toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
        </div>

      {/* 個人設定セクション */}
      <div>
        <h2 className="profile-section-title">個人設定</h2>
        <div className="profile-table-container">
          <table className="profile-table">
            <tbody>
              {/* ポイント表示 */}
              <tr>
                <td className="profile-label">
                  <span className="profile-label-with-help">
                    ポイント表示
                    <HelpTooltip ariaLabel="ポイント表示のヘルプ">
                      ヘッダーにポイント残高を表示するかどうかを設定できます。
                    </HelpTooltip>
                  </span>
                </td>
                <td className="profile-value">
                  <ToggleSwitch
                    checked={showPoints}
                    onChange={togglePointsVisibility}
                    label={showPoints ? '表示する' : '表示しない'}

                  />
                </td>
              </tr>

              {/* Canva連携 (canvaIntegration=false のブランドでは行ごと出さない) */}
              {brandConfig.features.canvaIntegration && (
              <tr>
                <td className="profile-label">
                  <span className="profile-label-with-help">
                    Canva連携
                    <HelpTooltip ariaLabel="Canva連携のヘルプ">
                      Canvaアカウントと連携して、素材を直接Canvaで編集できます。
                      {canvaStatus?.connected && (
                        <><br />別のCanvaアカウントに切り替える場合は、一度解除してから再連携してください。</>
                      )}
                    </HelpTooltip>
                  </span>
                </td>
                <td className="profile-value">
                  {isCanvaStatusLoading ? (
                    <span className="profile-setting-loading">読み込み中...</span>
                  ) : canvaStatus?.connected ? (
                    <div className="profile-setting-canva-connected">
                      <Label variant="green">連携済み</Label>
                      <button
                        type="button"
                        className="btn-text-danger"
                        onClick={async () => {
                          if (window.confirm('Canva連携を解除しますか？\n\n解除後は、再度連携するまでCanvaで編集できなくなります。')) {
                            try {
                              await disconnectCanvaMutation.mutateAsync();
                              toast.success('Canva連携を解除しました');
                            } catch (err) {
                              toast.error('Canva連携の解除に失敗しました');
                            }
                          }
                        }}
                        disabled={disconnectCanvaMutation.isPending}
                      >
                        {disconnectCanvaMutation.isPending ? '解除中...' : '連携を解除'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-modal"
                      onClick={startCanvaAuth}
                      disabled={isCanvaAuthenticating}
                    >
                      {isCanvaAuthenticating ? '連携中...' : 'Canvaと連携する'}
                    </button>
                  )}
                </td>
              </tr>
              )}

              {/* YouTube連携 */}
              <tr>
                <td className="profile-label">
                  <span className="profile-label-with-help">
                    YouTube連携
                    <HelpTooltip ariaLabel="YouTube連携のヘルプ">
                      ご自身のYouTubeアカウントと連携して、シナリオ動画を直接アップロードできます。
                      {youtubeStatus?.connected && (
                        <><br />別のYouTubeアカウントに切り替える場合は、一度解除してから再連携してください。</>
                      )}
                    </HelpTooltip>
                  </span>
                </td>
                <td className="profile-value">
                  {isYouTubeStatusLoading ? (
                    <span className="profile-setting-loading">読み込み中...</span>
                  ) : youtubeStatus?.connected ? (
                    <div className="profile-setting-canva-connected">
                      <Label variant="green">
                        {youtubeStatus.channel_title ? `連携済み: ${youtubeStatus.channel_title}` : '連携済み'}
                      </Label>
                      <button
                        type="button"
                        className="btn-text-danger"
                        onClick={async () => {
                          if (window.confirm('YouTube連携を解除しますか？\n\n解除後は、再度連携するまでYouTubeへアップロードできなくなります。')) {
                            try {
                              await disconnectYouTubeMutation.mutateAsync();
                              toast.success('YouTube連携を解除しました');
                            } catch (err) {
                              toast.error('YouTube連携の解除に失敗しました');
                            }
                          }
                        }}
                        disabled={disconnectYouTubeMutation.isPending}
                      >
                        {disconnectYouTubeMutation.isPending ? '解除中...' : '連携を解除'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-modal"
                      onClick={startYouTubeAuth}
                      disabled={isYouTubeAuthenticating}
                    >
                      {isYouTubeAuthenticating ? '連携中...' : 'YouTubeと連携する'}
                    </button>
                  )}
                </td>
              </tr>

              {/* ヘルプツールチップ表示 */}
              <tr
                id="help-tooltips-setting"
                className={shouldHighlightHelpTooltips ? 'profile-table-row--highlighted' : ''}
              >
                <td className="profile-label">
                  <span className="profile-label-with-help">
                    ヘルプを表示
                    <HelpTooltip ariaLabel="ヘルプ表示設定のヘルプ">
                      画面各所の「?」アイコンをクリックすると、機能の使い方を説明するヘルプが開きます。
                      操作に慣れてきたら、このスイッチをオフにしてアイコン自体を非表示にできます。
                    </HelpTooltip>
                  </span>
                </td>
                <td className="profile-value">
                  <ToggleSwitch
                    checked={showHelpTooltips}
                    onChange={toggleShowHelpTooltips}
                    disabled={isSavingHelpTooltips}
                    label={showHelpTooltips ? '表示する' : '表示しない'}

                  />
                </td>
              </tr>

              {/* ナビゲーションガイド再生 (HypeApp 等 navigationGuide 有効ブランドのみ) */}
              {brandConfig.features.navigationGuide && (
                <tr>
                  <td className="profile-label">
                    <span className="profile-label-with-help">
                      ナビゲーションガイド
                      <HelpTooltip ariaLabel="ナビゲーションガイドのヘルプ">
                        ホーム画面と主要なメニューの使い方をステップで案内します。初回ログイン時に自動表示されたガイドを、もう一度見たいときに使えます。
                      </HelpTooltip>
                    </span>
                  </td>
                  <td className="profile-value">
                    <button
                      type="button"
                      className="btn-modal"
                      onClick={handleReplayNavigationGuide}
                      disabled={isReplayingGuide}
                    >
                      {isReplayingGuide ? '準備中…' : 'ガイドを再生'}
                    </button>
                  </td>
                </tr>
              )}

              {/* セーフモード（ロック中＝partner 強制 or org 不許可 は行ごと非表示） */}
              {!safeModeLocked && (
              <tr>
                <td className="profile-label">
                  <span className="profile-label-with-help">
                    セーフモード
                    <HelpTooltip ariaLabel="セーフモードのヘルプ">
                      一部の表現が制限されます。解除すると、より幅広い表現が可能になります。
                    </HelpTooltip>
                  </span>
                </td>
                <td className="profile-value">
                  <ToggleSwitch
                    checked={safeMode}
                    onChange={toggleSafeMode}
                    disabled={isSavingSafeMode}
                    label={safeMode ? '制限されています' : '解除されています'}

                  />
                </td>
              </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* ACP クリエイターセクション (自分がクリエイター登録されている場合のみ表示) */}
      {brandConfig.features.creatorProgram && <CreatorSection />}

      {/* セーフモード解除確認ダイアログ（ロック中は解除導線が無いため不要） */}
      {!safeModeLocked && (
      <ConfirmDialog
        isOpen={showSafeModeConfirm}
        title="セーフモードを解除しますか？"
        message="解除すると、より幅広い表現の画像が生成可能になります。この設定はいつでも変更できます。"
        confirmLabel="解除する"
        cancelLabel="キャンセル"
        variant="warning"
        isLoading={isSavingSafeMode}
        onConfirm={async () => {
          await handleSafeModeChange(false);
          setShowSafeModeConfirm(false);
        }}
        onCancel={() => setShowSafeModeConfirm(false)}
      />
      )}

      {/* 顔クロップモーダル */}
      {showCropModal && selectedFile && (
        <FaceCropModal
          imageFile={selectedFile}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      {/* AI俳優生成ウィザード */}
      <ActorGenerationWizard
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        onComplete={handleWizardComplete}
        generatePersonApi={generatePerson}
        generateCharacterSheetApi={generateCharacterSheet}
        suggestNameApi={suggestName}
        checkNameApi={checkExclusiveActorName}
        initialData={wizardInitialData}
      />

      {/* AI俳優詳細モーダル */}
      <ActorDetailModal
        isOpen={isActorDetailOpen}
        onClose={() => setIsActorDetailOpen(false)}
        actor={selectedActor}
        onEdit={handleEditActor}
      />

      {/* AI俳優編集モーダル */}
      <ActorFormModal
        isOpen={isActorFormOpen}
        onClose={() => {
          setIsActorFormOpen(false);
          setEditingActor(null);
        }}
        onSuccess={handleActorFormSuccess}
        actor={editingActor}
        updateFn={({ actorId, request }) => updateActorMutation.mutateAsync({ actorId, request })}
        deleteFn={(actorId) => deleteActorMutation.mutateAsync(actorId)}
        onTriggerFaceScan={(actorId) => triggerExclusiveActorFaceScan(actorId)}
        titlePrefix="専属"
      />

      {/* 全身写真ライトボックス */}
      {lightboxPhotoUrl && (
        <div
          className="full-body-lightbox-overlay"
          onClick={() => setLightboxPhotoUrl(null)}
        >
          <button
            type="button"
            className="full-body-lightbox-close"
            onClick={() => setLightboxPhotoUrl(null)}
          >
            ×
          </button>
          <img
            src={lightboxPhotoUrl}
            alt="全身写真"
            className="full-body-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
